<template>
  <div class="reschedule-scheduler" v-loading="isLoading" element-loading-text="載入中...">
    <div class="reschedule-scheduler__calendar-section statusCalendar">
      <el-row>
        <el-col :span="7" class="reschedule-scheduler__summary">
          <div class="reschedule-scheduler__summary-title">
            【原預約資訊】<template v-if="orderPetCount"> ｜ <span class="reschedule-scheduler__pet-count">{{ orderPetCount }}</span> 隻</template>
          </div>
          <div v-if="stayRangeText" class="reschedule-scheduler__summary-line">日期： {{ stayRangeText }}</div>
          <div v-if="roomTitle" class="reschedule-scheduler__summary-line">房型： {{ roomTitle }}</div>
          <div v-if="orderPetNames" class="reschedule-scheduler__summary-line">寵物： {{ orderPetNames }}</div>
        </el-col>
        <el-col :span="17">
          <Calendar :is-loading="isLoading" :initial-date="calendarInitialDate" @week-change="handleWeekChange" />
        </el-col>
      </el-row>
    </div>

    <HotelRescheduleMode
      v-if="roomTypeList.length"
      ref="rescheduleMode"
      :room-type-list="roomTypeList"
      :container-uuid="containerId"
      :initial-bookings="initialBookings"
      :order-guests="orderGuests"
      @confirm="$emit('confirm', $event)"
      @selection-change="onSelectionChange"
    />

    <div v-else-if="!isLoading && hasFetched" class="reschedule-scheduler__empty">
      該服務地點，目前尚未建立任何房型，請先至寵物住宿分頁設定
    </div>
  </div>
</template>

<script>
import Calendar from '../../hotelCalendar.vue'
import HotelRescheduleMode from '../../hotelRescheduleMode.vue'
import api from '@/src/router/views/schedule/hotel/api'
import formatDate from '@utils/format-date'
import { formatStayNightLabel, resolveHotelStay } from '@utils/format-hotel-stay'
import { getEarliestBookingDate, resolveWeekRange } from '@utils/hotel-room-selection'
import { mapGetters } from 'vuex'

export default {
  name: 'HotelRescheduleScheduler',
  components: {
    Calendar,
    HotelRescheduleMode,
  },
  props: {
    productId: {
      type: [String, Number],
      required: true,
    },
    containerId: {
      type: String,
      default: '',
    },
    initialBookings: {
      type: Array,
      default: () => [],
    },
    orderGuests: {
      type: Array,
      default: () => [],
    },
    roomTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isLoading: false,
      hasFetched: false,
      roomTypeList: [],
      currentWeekStart: '',
      currentWeekEnd: '',
    }
  },
  computed: {
    ...mapGetters('hotel/setting', ['showCheckoutDate']),
    orderPetCount() {
      return Array.isArray(this.orderGuests) ? this.orderGuests.length : 0
    },
    orderPetNames() {
      return (this.orderGuests || []).map((g) => g.name).filter(Boolean).join('、')
    },
    stayRangeText() {
      const stay = resolveHotelStay({ bookings: this.initialBookings, showCheckoutDate: this.showCheckoutDate })
      if (!stay) return ''
      const checkIn = formatDate(stay.checkIn, 'date')
      const checkout = formatDate(stay.checkout, 'MM/DD')
      return `${checkIn} - ${checkout} (${formatStayNightLabel(stay.nights, this.showCheckoutDate)})`
    },
    orderFirstStayDate() {
      return getEarliestBookingDate(this.initialBookings)
    },
    calendarInitialDate() {
      return this.orderFirstStayDate
    },
  },
  mounted() {
    if (this.orderFirstStayDate && !this.hasFetched) {
      const range = resolveWeekRange(this.orderFirstStayDate)
      if (range.start && range.end) {
        this.currentWeekStart = range.start
        this.currentWeekEnd = range.end
        this.fetchRoomList()
      }
    }
  },
  methods: {
    reset() {
      this.$refs.rescheduleMode?.resetSelection()
    },
    confirmSelection() {
      this.$refs.rescheduleMode?.onClickConfirm()
    },
    onSelectionChange(state) {
      this.$emit('selection-change', state)
    },
    handleWeekChange(range) {
      if (range.start === this.currentWeekStart && range.end === this.currentWeekEnd) {
        return
      }
      this.currentWeekStart = range.start
      this.currentWeekEnd = range.end
      this.fetchRoomList()
    },
    async fetchRoomList() {
      if (!this.currentWeekStart || !this.currentWeekEnd || !this.productId) {
        return
      }
      this.isLoading = true
      try {
        const res = await api.room.getHotelBookings(this.productId, {
          startDate: this.currentWeekStart,
          endDate: this.currentWeekEnd,
        })
        this.roomTypeList = (res || []).map((item) => ({
          ...item,
          isViewMore: true,
        }))
        this.hasFetched = true
        if (!this.roomTypeList.length) {
          this.$emit('selection-change', { active: false })
        }
      } catch (error) {
        console.error('房間列表獲取失敗:', error)
        this.$message.error('抓取房間列表失敗，請再嘗試一次')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.reschedule-scheduler {
  background-color: #f2f2f2;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
}

.reschedule-scheduler__calendar-section {
  background-color: #ffffff;
  margin: 0 0 16px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 8px 12px;
}

.reschedule-scheduler__summary {
  background-color: #ffffff;
  height: auto;
  font-weight: 400;
  color: #000000;
  text-align: left;
  padding: 8px 12px;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.reschedule-scheduler__summary-title {
  font-size: large;
  font-weight: 500;
  color: #000000;
  text-align: left;
  width: 100%;
  margin-bottom: 4px;
}

.reschedule-scheduler__summary-line {
  font-size: 14px;
  color: #000000;
  text-align: left;
  width: 100%;
}

.reschedule-scheduler__pet-count {
  color: #ea6a2a;
  font-size: large;
  font-weight: 500;
}

.reschedule-scheduler__empty {
  width: 100%;
  text-align: center;
  margin-top: 48px;
  font-size: 1.1rem;
  color: #6c6b6b;
}

.statusCalendar {
  ::v-deep .el-col:not(.reschedule-scheduler__summary) {
    text-align: center;
  }
}
</style>
